import { Box, ScrollArea } from '@mantine/core'
import { useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { Titlebar } from '../Titlebar'
import styles from './AppPage.module.css'
import { AppPageProps } from './types'

export function AppPage({
  title,
  subtitle,
  onBack,
  backTarget,
  rightSection,
  showBackButton = true,
  disablePadding = false,
  autoHideHeader = true,
  fullHeight = false,
  children,
}: AppPageProps) {
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [titlebarHeight, setTitlebarHeight] = useState(60)
  const lastScrollY = useRef(0)
  const viewportRef = useRef<HTMLDivElement>(null)
  const titlebarRef = useRef<HTMLDivElement>(null)

  // Measure titlebar height dynamically
  useLayoutEffect(() => {
    if (!titlebarRef.current) return

    const measureHeight = () => {
      if (titlebarRef.current) {
        setTitlebarHeight(titlebarRef.current.offsetHeight)
      }
    }

    measureHeight()

    const observer = new ResizeObserver(measureHeight)
    observer.observe(titlebarRef.current)

    return () => observer.disconnect()
  }, [])

  // Compute the actual back handler
  const handleBack = () => {
    if (onBack) {
      // If explicit onBack handler is provided, use it
      onBack()
    } else if (canGoBack) {
      // If we can go back in history, use browser back
      router.history.back()
    } else if (backTarget) {
      // Fall back to navigating to backTarget
      navigate({ to: backTarget })
    }
  }

  // Only show back button if either onBack or backTarget is provided
  const shouldShowBackButton = showBackButton && !!(onBack || backTarget)

  // Handle scroll position changes for auto-hide header
  const handleScrollPositionChange = ({ y }: { y: number }) => {
    if (!autoHideHeader) return

    if (y < 50) {
      // Always show header near the top
      setIsHeaderVisible(true)
    } else {
      // Check if we're near the bottom to prevent flickering from elastic/rubber-band scrolling
      const viewport = viewportRef.current
      if (viewport) {
        const scrollHeight = viewport.scrollHeight
        const clientHeight = viewport.clientHeight
        const distanceFromBottom = scrollHeight - y - clientHeight

        // Prevent state changes when within 50px of bottom to avoid flickering and jumping
        if (distanceFromBottom < 50) {
          lastScrollY.current = y
          return
        }
      }

      // Only toggle if scrolled more than 10px to prevent flickering from bounce/overscroll
      const scrollDelta = Math.abs(y - lastScrollY.current)
      if (scrollDelta < 10) return

      if (y > lastScrollY.current) {
        // Scrolling down - hide header
        setIsHeaderVisible(false)
      } else {
        // Scrolling up - show header
        setIsHeaderVisible(true)
      }
    }

    lastScrollY.current = y
  }

  return (
    <Box className={styles.container}>
      {title && (
        <Box ref={titlebarRef} className={styles.titlebarWrapper}>
          {React.isValidElement(title) ? (
            title
          ) : (
            <Titlebar
              title={title}
              subtitle={subtitle}
              onBack={shouldShowBackButton ? handleBack : undefined}
              rightSection={rightSection}
              showBackButton={shouldShowBackButton}
              isVisible={isHeaderVisible}
            />
          )}
        </Box>
      )}

      {fullHeight ? (
        <Box
          className={styles.fullHeightWrapper}
          style={{
            paddingTop: titlebarHeight,
          }}
        >
          {children}
        </Box>
      ) : (
        <ScrollArea
          viewportRef={viewportRef}
          className={styles.scrollArea}
          onScrollPositionChange={handleScrollPositionChange}
        >
          <Box
            className={
              disablePadding
                ? styles.content
                : `${styles.content} ${styles.contentWithPadding}`
            }
            style={{
              paddingTop: disablePadding
                ? titlebarHeight
                : `calc(${titlebarHeight}px + var(--mantine-spacing-md))`,
            }}
          >
            {children}
          </Box>
        </ScrollArea>
      )}
    </Box>
  )
}

export type { AppPageProps } from './types'
